import { Button, Tooltip } from "antd";
import { TableOutlined } from "@ant-design/icons";
import { ContentViews } from "./useContentView";
import { MeasureTable, MeasureTableControls } from "./MeasureTable";
import { VisualizationProps } from "./visualization-props";
import { PivotTableV2, PivotTableV2Controls } from "./PivotTableV2";
import { ExploreContext } from "../useExploreContext";
import { PivotTableIcon } from "src/components/PivotTableIcon";
import { NestedTableIcon } from "src/components/NestedTableIcon";
import { EntitiesTable, EntitiesTableControls } from "./EntitiesTable";

// Helper function to compute the new pivot group index
function getNewPivotGroupIndex(
  currentPivotGroupIndex: number | undefined,
  groupBys: any[]
): number | undefined {
  return currentPivotGroupIndex !== undefined &&
    currentPivotGroupIndex < groupBys.length
    ? currentPivotGroupIndex
    : groupBys.length > 0
    ? 0
    : undefined;
}

// Shared layout selector component
export function TableLayoutSelector({
  currentLayout,
  onLayoutChange,
  context,
}: {
  currentLayout: string;
  onLayoutChange: (
    layout: "flat" | "pivot" | "nested",
    contentView: ContentViews,
    newConfig?: any
  ) => void;
  context: VisualizationProps["context"];
}) {
  const hasGroups = (context.params.state.groupBys ?? []).length > 0;
  const noGroupsMessage =
    "This view requires at least one group by field to be selected";

  return (
    <div className="section">
      <div className="section-header">Layout</div>
      <div className="layout-control-grid">
        <Button
          type="default"
          onClick={() => {
            onLayoutChange("flat", ContentViews.dataTable, { columns: [] });
          }}
          className={`layout-control-button ${
            currentLayout === "flat" ? "selected" : ""
          }`}
        >
          <TableOutlined />
          <span
            className="layout-control-button-label"
            style={{ marginInlineStart: 0 }}
          >
            Flat
          </span>
        </Button>
        <Tooltip title={hasGroups ? "" : noGroupsMessage}>
          <Button
            type="default"
            disabled={!hasGroups}
            onClick={() => {
              onLayoutChange("nested", ContentViews.nestedTable, {
                columns: [],
              });
            }}
            className={`layout-control-button ${
              currentLayout === "nested" ? "selected" : ""
            }`}
          >
            <NestedTableIcon size={20} />
            <span className="layout-control-button-label">Tree</span>
          </Button>
        </Tooltip>
        <Tooltip title={hasGroups ? "" : noGroupsMessage}>
          <Button
            type="default"
            disabled={!hasGroups}
            onClick={() => {
              onLayoutChange("pivot", ContentViews.pivotTable, {
                columns: [],
                pivotGroupIndex: getNewPivotGroupIndex(
                  context.view.config?.pivotGroupIndex,
                  context.params.state.groupBys || []
                ),
              });
            }}
            className={`layout-control-button ${
              currentLayout === "pivot" ? "selected" : ""
            }`}
          >
            <PivotTableIcon size={20} />
            <span className="layout-control-button-label">Pivot</span>
          </Button>
        </Tooltip>
      </div>
    </div>
  );
}

export function UnifiedTableVisualization(props: VisualizationProps) {
  const { view } = props.context;
  const layout = view.config?.layout || "flat";

  const hasGroups = (props.context.params.state.groupBys ?? []).length > 0;

  if (!hasGroups) {
    return <EntitiesTable {...props} />;
  }

  switch (layout) {
    case "nested": {
      // For nested view, use PivotTableV2 with measures as columns (no row grouping)
      const nestedConfig = {
        ...view.config,
        pivotBy: "", // Set no row grouping (empty string for none)
        pivotGroupIndex: undefined, // Ensure no pivot grouping
        measuresAsColumns: true, // Ensure measures appear as columns
      };
      return <PivotTableV2 context={props.context} config={nestedConfig} />;
    }
    case "pivot": {
      return <PivotTableV2 context={props.context} config={view.config} />;
    }
    case "flat": {
      return <MeasureTable {...props} />;
    }
    default: {
      return <EntitiesTable {...props} />;
    }
  }
}

export function UnifiedTableVisualizationControls(props: {
  context: ExploreContext;
}) {
  const { view } = props.context;
  const layout = view.config?.layout || "flat";
  // Check if groupBys exists and has length greater than 0
  const hasGroups = (props.context.params.state.groupBys ?? []).length > 0;

  // If there are no groups, always show entities table controls
  if (!hasGroups) {
    return <EntitiesTableControls context={props.context} />;
  }
  // Only show specialized controls if we have groups
  switch (layout) {
    case "pivot": {
      return (
        <PivotTableV2Controls
          context={props.context}
          controlsConfig={{
            showSortingControls: true,
          }}
        />
      );
    }
    case "nested": {
      return (
        <PivotTableV2Controls
          context={props.context}
          controlsConfig={{
            showGroupSelector: false,
            showPaging: true,
            showWrapping: true,
            showCompare: true,
            showLayoutSelector: true,
            showRollupMeasureSelector: false,
            showSortingControls: true,
          }}
        />
      );
    }
    default: {
      return <MeasureTableControls context={props.context} />;
    }
  }
}
